import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  AccountBalance,
  ArrowUpward,
  ArrowDownward,
  Loyalty,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from "@mui/icons-material";
// styled import commented out (uncomment if solar banner is reactivated)
// import { styled } from "@mui/material/styles";
import { formatNumber } from "../../utils/formatNumber";
import { successAlert, errorAlert } from "../../utils/swalConfig";
import DepositModal from "./DepositModal";
import WithdrawalModal from "./WithdrawalModal";
import { useNavigate } from "react-router-dom";
import NotificationTicker from "./NotificationTicker";
import DailyReward from "./DailyReward";

// SolarAdBanner component definition kept for future use (commented out to prevent unused variable warning)
/*
const SolarAdBanner = styled(Card)(({ theme }) => ({
  backgroundImage: "linear-gradient(to right, #00796b, #009688)",
  color: "#fff",
  position: "relative",
  overflow: "hidden",
  cursor: "pointer",
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[8],
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "150px",
    height: "150px",
    background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "50%",
    transform: "translate(30%, -30%)",
  },
  [theme.breakpoints.down("sm")]: {
    // Mobile-specific styles
    paddingBottom: theme.spacing(1),
    "&::before": {
      // Adjust background element for mobile
      width: "100px",
      height: "100px",
    },
  },
}));
*/

const Dashboard = () => {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState({
    availableBalance: 0,
    bonusWallet: 0,
  });
  const [userData, setUserData] = useState({
    firstName: "",
    referralCode: "",
    referralLink: "",
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

  useEffect(() => {
    fetchUserData();
    fetchRecentTransactions();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem("jwt");
      const response = await api.get("/users/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

      localStorage.setItem("userRole", response.data.data.user.role);

      const firstName = response.data.data.user.name.split(" ")[0];
      const referralLink = `${window.location.origin}/signup?ref=${response.data.data.user.referralCode}`;

      setUserData({
        firstName,
        referralCode: response.data.data.user.referralCode,
        referralLink,
      });

      setWallets({
        availableBalance: response.data.data.user.availableBalance,
        bonusWallet: response.data.data.user.bonusWallet,
      });
      setLoading(false);
    } catch (err) {
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to fetch user data"
      );
      setLoading(false);
    }
  };

  const fetchRecentTransactions = async () => {
    try {
      const token = localStorage.getItem("jwt");
      const response = await api.get("/users/transactions/recent", {
        headers: { Authorization: `Bearer ${token}` },
      });

      setTransactions(response.data.data.transactions);
    } catch (err) {
      errorAlert("Error", "Failed to fetch recent transactions");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    successAlert("Copied!", "Referral link copied to clipboard");
  };

  const handleDeposit = () => {
    setIsDepositModalOpen(true);
  };

  const handleWithdraw = () => {
    setIsWithdrawModalOpen(true);
  };

  const handleTransactionSuccess = () => {
    fetchUserData(); // Refresh wallet balances after transaction
  };

  const handleLogout = () => {
    // Clear JWT token
    localStorage.removeItem("jwt");
    // Navigate to login page
    navigate("/login");
  };

  // Check if user is admin
  const checkIsAdmin = () => {
    const userRole = localStorage.getItem("userRole");
    return userRole === "admin";
  };

  return (
    <Box>
      <NotificationTicker />
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="outlined"
          color="error"
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
        >
          Logout
        </Button>
      </Box>
      {loading ? (
        <LinearProgress />
      ) : (
        <>
          <Typography variant="h4" sx={{ mb: 3 }}>
            Hello, {userData.firstName} 👋
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <AccountBalance sx={{ color: "primary.main", mr: 1 }} />
                    <Typography variant="h6">Available Balance</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    {formatNumber(wallets.availableBalance)}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<ArrowUpward />}
                    onClick={handleDeposit}
                  >
                    Deposit
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Loyalty sx={{ color: "secondary.main", mr: 1 }} />
                    <Typography variant="h6">Wallet Balance</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    {formatNumber(wallets.bonusWallet)}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<ArrowDownward />}
                    onClick={handleWithdraw}
                  >
                    Withdraw
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Referral Section */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6">Your Referral Link</Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => copyToClipboard(userData.referralLink)}
                      sx={{ ml: "auto" }}
                    >
                      Copy Link
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      fontSize: "0.875rem",
                      fontFamily: "monospace",
                    }}
                  >
                    {userData.referralLink}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Share this link with friends and earn up to ₦75,000 when
                    they activate their account
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <DailyReward />

            {/* Recent Transactions */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3 }}>
                    Recent Transactions
                  </Typography>
                  {transactions.length > 0 ? (
                    <Grid container spacing={2}>
                      {transactions.map((transaction) => (
                        <Grid item xs={12} sm={6} key={transaction._id}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  color="textSecondary"
                                >
                                  {new Date(
                                    transaction.createdAt
                                  ).toLocaleString("en-NG", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                    hour12: true,
                                  })}
                                </Typography>
                                <Chip
                                  label={transaction.status}
                                  size="small"
                                  color={
                                    transaction.status === "successful"
                                      ? "success"
                                      : transaction.status === "pending"
                                      ? "warning"
                                      : "error"
                                  }
                                />
                              </Box>
                              <Typography variant="h6">
                                ₦{transaction.amount.toLocaleString()}
                              </Typography>
                              <Typography color="textSecondary">
                                {transaction.type === "deposit"
                                  ? "Deposit"
                                  : "Withdrawal"}
                              </Typography>
                              {transaction.type === "withdrawal" && (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {transaction.recipientBank} -{" "}
                                  {transaction.recipientAccountNumber}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography color="textSecondary" align="center">
                      No recent transactions
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Add Modals */}
          <DepositModal
            open={isDepositModalOpen}
            onClose={() => setIsDepositModalOpen(false)}
            onSuccess={handleTransactionSuccess}
          />

          <WithdrawalModal
            open={isWithdrawModalOpen}
            onClose={() => setIsWithdrawModalOpen(false)}
            onSuccess={handleTransactionSuccess}
            availableBalance={wallets.availableBalance}
            bonusWallet={wallets.bonusWallet}
          />

          {checkIsAdmin() && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AdminPanelSettingsIcon />}
              onClick={() => navigate("/admin/dashboard")}
              sx={{
                position: "fixed",
                bottom: 20,
                right: 20,
                borderRadius: "50px",
                padding: "10px 20px",
                boxShadow: 3,
              }}
            >
              Admin Panel
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default Dashboard;
